import { Injectable, inject } from "@angular/core";
import { Auth, user } from "@angular/fire/auth";
import { Firestore, doc, getDoc, collection, query, where, getDocs } from "@angular/fire/firestore";
import { Observable, from, of, switchMap, map, combineLatest } from "rxjs";
import { shareReplay } from "rxjs/operators";
import { UserRole, UserRoles, UserWithRoles } from "../models/user-role.model";

@Injectable({
	providedIn: "root",
})
export class RoleService {
	private auth: Auth = inject(Auth);
	private firestore: Firestore = inject(Firestore);
	private user$ = user(this.auth).pipe(shareReplay(1));

	constructor() {}

	/**
	 * Get the current user with their roles
	 */
	getCurrentUserWithRoles(): Observable<UserWithRoles | null> {
		return this.user$.pipe(
			switchMap((user) => {
				if (!user) return of(null);
				return this.getUserRoles(user.uid).pipe(
					map(
						(roles) =>
							({
								uid: user.uid,
								// Mapped off the Firebase Auth user, whose own
								// fields are camelCase, into the document's
								// spelling. Keeping Auth's names here is what
								// produced two spellings of the same value.
								email: user.email ?? "",
								displayName: user.displayName ?? undefined,
								photo_url: user.photoURL ?? undefined,
								roles,
							}) as UserWithRoles,
					),
				);
			}),
		);
	}

	/**
	 * Get roles for a specific user
	 */
	getUserRoles(userId: string): Observable<UserRoles> {
		return from(getDoc(doc(this.firestore, `users/${userId}`))).pipe(
			map((userDoc) => {
				if (!userDoc.exists()) {
					return {};
				}
				const userData = userDoc.data();
				return userData["roles"] || {};
			}),
		);
	}

	/**
	 * Check if the current user has a specific role
	 */
	hasRole(user: UserWithRoles, role: UserRole): boolean {
		if (!user || !user.roles) return false;
		if (Array.isArray(user.roles)) {
			return user.roles.includes(role);
		}
		return user.roles[role] === true;
	}

	/**
	 * Check if the current user has any of the specified roles
	 */
	hasAnyRole(roles: UserRole[]): Observable<boolean> {
		return this.getCurrentUserWithRoles().pipe(
			map((user) => {
				if (!user) return false;
				return roles.some((role) => this.hasRole(user, role));
			}),
		);
	}

	/**
	 * Get a list of all available roles
	 */
	getAllRoles(): UserRole[] {
		return Object.values(UserRole);
	}

	/**
	 * Get a friendly display name for a role
	 */
	getRoleDisplayName(role: UserRole): string {
		const roleNames: Partial<Record<UserRole, string>> = {
			[UserRole.ADMIN]: "Global Admin",
			[UserRole.ORG_ADMIN]: "Organization Admin",
			[UserRole.RESPONDENT]: "Respondent",
		};

		return roleNames[role] ?? role;
	}

	/**
	 * Check if the current user has a global role
	 * @param role The role to check
	 * @returns Observable<boolean> True if the user has the role
	 */
	hasGlobalRole(role: UserRole): Observable<boolean> {
		return this.user$.pipe(
			switchMap((user) => {
				if (!user) return of(false);

				return from(getDoc(doc(this.firestore, `users/${user.uid}`))).pipe(
					map((userDoc) => {
						if (!userDoc.exists()) return false;
						const userData = userDoc.data();
						// Inactive accounts (active === false) hold no global role.
						return (
							userData["active"] !== false &&
							!!userData["roles"] &&
							userData["roles"][role] === true
						);
					}),
				);
			}),
		);
	}

	/**
	 * Check if the current user has any of the specified global roles
	 * @param roles Array of roles to check
	 * @returns Observable<boolean> True if the user has any of the roles
	 */
	hasAnyGlobalRole(roles: UserRole[]): Observable<boolean> {
		return combineLatest(roles.map((role) => this.hasGlobalRole(role))).pipe(
			map((results) => results.some((hasRole) => hasRole)),
		);
	}

	/**
	 * Check if the current user has a specific role in an organization
	 * @param organizationId The organization ID
	 * @param role The role to check
	 * @returns Observable<boolean> True if the user has the role in the organization
	 */
	hasRoleInOrg(organizationId: string, role: UserRole): Observable<boolean> {
		// First check if the user has the global admin role
		return this.hasGlobalRole(UserRole.ADMIN).pipe(
			switchMap((isGlobalAdmin) => {
				// If the user is a global admin, they have all roles
				if (isGlobalAdmin) return of(true);

				// Otherwise, check for the role in the specific organization
				return this.user$.pipe(
					switchMap((user) => {
						if (!user) return of(false);

						const membershipId = `${organizationId}_${user.uid}`;
						return from(
							getDoc(doc(this.firestore, `organizationUsers/${membershipId}`)),
						).pipe(
							map((memberDoc) => {
								if (!memberDoc.exists()) return false;
								const memberData = memberDoc.data();
								return memberData["roles"] && memberData["roles"][role] === true;
							}),
						);
					}),
				);
			}),
		);
	}

	/**
	 * Check if the current user has any of the specified roles in an organization
	 * @param organizationId The organization ID
	 * @param roles Array of roles to check
	 * @returns Observable<boolean> True if the user has any of the roles in the organization
	 */
	hasAnyRoleInOrg(organizationId: string, roles: UserRole[]): Observable<boolean> {
		return combineLatest(roles.map((role) => this.hasRoleInOrg(organizationId, role))).pipe(
			map((results) => results.some((hasRole) => hasRole)),
		);
	}

	/**
	 * Get all organizations where the current user has a specific role
	 * @param role The role to check
	 * @returns Observable<string[]> Array of organization IDs
	 */
	getOrganizationsWithRole(role: UserRole): Observable<string[]> {
		return this.user$.pipe(
			switchMap((user) => {
				if (!user) return of([]);

				// First check if the user is a global admin
				return this.hasGlobalRole(UserRole.ADMIN).pipe(
					switchMap((isGlobalAdmin) => {
						if (isGlobalAdmin) {
							// If global admin, get all organizations
							return from(getDocs(collection(this.firestore, "organizations"))).pipe(
								map((snapshot) => snapshot.docs.map((doc) => doc.id)),
							);
						} else {
							// Otherwise, get organizations where the user has the specific role
							const membershipQuery = query(
								collection(this.firestore, "organizationUsers"),
								where("user_id", "==", user.uid),
							);

							return from(getDocs(membershipQuery)).pipe(
								map((snapshot) => {
									return snapshot.docs
										.filter((doc) => {
											const data = doc.data();
											return data["roles"] && data["roles"][role] === true;
										})
										.map((doc) => doc.data()["organization_id"]);
								}),
							);
						}
					}),
				);
			}),
		);
	}

	/**
	 * Get all roles the current user has in a specific organization
	 * @param organizationId The organization ID
	 * @returns Observable<UserRole[]> Array of roles
	 */
	getRolesInOrg(organizationId: string): Observable<UserRole[]> {
		return this.user$.pipe(
			switchMap((user) => {
				if (!user) return of([]);

				// First check if the user is a global admin
				return this.hasGlobalRole(UserRole.ADMIN).pipe(
					switchMap((isGlobalAdmin) => {
						if (isGlobalAdmin) {
							// If global admin, they have all roles
							return of(Object.values(UserRole));
						} else {
							// Otherwise, get the specific roles for this organization
							const membershipId = `${organizationId}_${user.uid}`;
							return from(
								getDoc(doc(this.firestore, `organizationUsers/${membershipId}`)),
							).pipe(
								map((memberDoc) => {
									if (!memberDoc.exists()) return [];
									const memberData = memberDoc.data();
									if (!memberData["roles"]) return [];

									return Object.entries(memberData["roles"])
										.filter(([_, hasRole]) => hasRole === true)
										.map(([role, _]) => role as UserRole);
								}),
							);
						}
					}),
				);
			}),
		);
	}

	/**
	 * Get all organizations the current user is a member of
	 * @returns Observable<string[]> Array of organization IDs
	 */
	getUserOrganizations(): Observable<string[]> {
		return this.user$.pipe(
			switchMap((user) => {
				if (!user) return of([]);

				const membershipQuery = query(
					collection(this.firestore, "organizationUsers"),
					where("user_id", "==", user.uid),
				);

				return from(getDocs(membershipQuery)).pipe(
					map((snapshot) => snapshot.docs.map((doc) => doc.data()["organization_id"])),
				);
			}),
		);
	}
}
